// Constants
import { UnassignedUserId } from "@ms/uno-constants/lib/local/AppConstants";
import { DueDateCategory } from "@ms/uno-constants/lib/local/DateConstants";
import { TasksWithoutGoalAssociationGroup, UnbucketedTaskGroup, UnlabelledTaskGroup } from "@ms/uno-constants/lib/local/GroupingConstants";
import { DynamicFilterField, FilterFields, NoCustomFieldValueFilterKey, StatusFilterOption } from "../constants/FilterConstants";
// Utilities
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { formatShortDate, getSystemTimeZone, getTodayPlainDate, isInCurrentWeek, isInNextWeek } from "@ms/uno-utilities/lib/local/DateUtilities-temporal";
import { Temporal } from "temporal-polyfill-lite";
import { CustomFieldValueType } from "../service/planner/enumerations/IPlannerEnumerations";
import { ConvertPriorityToPriorityName } from "./ConversionUtilities";
import { resolveCustomFieldDefinitionIdFromFilterField } from "./CustomFieldDefinitionUtilities";
import { getDueDateCategory } from "./TaskGroupingUtilities";
/**
 * Applies a complete filter set to a task
 * @param task The task to filter
 * @param filterSet The set of filters to apply
 * @param timeZone Optional timezone string. Pass this when processing many tasks to avoid repeated timezone resolution.
 * @param todayPlainDate Optional pre-computed today's PlainDate. Pass this when processing many tasks to avoid repeated computation.
 * @param choiceDisplayNames Map from choice ID to display name, used for keyword search on SingleChoiceText fields.
 * @param cfdValueTypes Map from custom field definition ID to its value type for the current plan.
 * Used to determine how to compare a task's custom field value against a filter value.
 * @returns True if task satisfies all filters in the set
 */ export function applyEntityFilter(task, filterSet, timeZone = getSystemTimeZone(), todayPlainDate = getTodayPlainDate(timeZone), choiceDisplayNames = new Map(), cfdValueTypes = new Map()) {
    for (const filter of filterSet.filters){
        if (filter.active === false) {
            continue;
        }
        if (!applyTaskFilter(task, filter, timeZone, todayPlainDate, choiceDisplayNames, cfdValueTypes)) {
            return false;
        }
    }
    return true;
}
/**
 * Apply a single filter to a task
 * @param task Task to check
 * @param filter Filter to apply
 * @param timeZone Optional timezone string. Pass this when processing many tasks to avoid repeated timezone resolution.
 * @param todayPlainDate Optional pre-computed today's PlainDate. Pass this when processing many tasks to avoid repeated computation.
 * @param choiceDisplayNames Map from choice ID to display name, used for keyword search on SingleChoiceText fields.
 * @param cfdValueTypes Map from custom field definition ID to its value type for the current plan.
 * Used to determine how to compare a task's custom field value against a filter value.
 * @returns True if task satisfies the filter
 */ export function applyTaskFilter(task, filter, timeZone = getSystemTimeZone(), todayPlainDate = getTodayPlainDate(timeZone), choiceDisplayNames = new Map(), cfdValueTypes = new Map()) {
    switch(filter.field){
        case FilterFields.UserAssignments:
            return filter.values.some((value)=>{
                if (value === UnassignedUserId && task.userAssignments.length === 0) {
                    return true;
                }
                return task.userAssignments.some((ua)=>ua.user.id === value);
            });
        case FilterFields.BucketId:
            return filter.values.some((value)=>{
                if (value === UnbucketedTaskGroup) {
                    return task.bucketId === null;
                }
                return task.bucketId === value;
            });
        case FilterFields.DueDateTime:
            return filter.values.some((value)=>{
                if (value === DueDateCategory.SomeDate) {
                    return !!task.dueDateTime?.temporal;
                }
                const duePlainDate = task.dueDateTime?.getLocalDate(timeZone) ?? null;
                // ThisWeek/NextWeek filters should include all dates within the week range
                // (including today, tomorrow, and overdue dates that fall within the week).
                // getDueDateCategory returns mutually exclusive categories, so we check directly.
                if (value === DueDateCategory.ThisWeek) {
                    return isInCurrentWeek(duePlainDate, todayPlainDate);
                }
                if (value === DueDateCategory.NextWeek) {
                    return isInNextWeek(duePlainDate, todayPlainDate);
                }
                const taskDueDateCategory = getDueDateCategory(duePlainDate, todayPlainDate);
                return taskDueDateCategory === value;
            });
        case FilterFields.FinishDateTime:
            return filter.values.some((value)=>{
                if (value === DueDateCategory.SomeDate) {
                    return !!task.finishDateTime?.temporal;
                }
                const finishPlainDate = task.finishDateTime?.getLocalDate(timeZone) ?? null;
                if (value === DueDateCategory.ThisWeek) {
                    return isInCurrentWeek(finishPlainDate, todayPlainDate);
                }
                if (value === DueDateCategory.NextWeek) {
                    return isInNextWeek(finishPlainDate, todayPlainDate);
                }
                const taskFinishDateCategory = getDueDateCategory(finishPlainDate, todayPlainDate);
                return taskFinishDateCategory === value;
            });
        case FilterFields.Priority:
            return filter.values.some((value)=>{
                return ConvertPriorityToPriorityName(task.priority) === ConvertPriorityToPriorityName(value);
            });
        case FilterFields.Status:
            return filter.values.some((value)=>{
                if (value === StatusFilterOption.Late) {
                    return task.isLate();
                }
                return task.status === value;
            });
        case FilterFields.AppliedCategoryIds:
            return filter.values.some((value)=>{
                if (value === UnlabelledTaskGroup) {
                    return task.planView.appliedCategoryIds.length === 0;
                }
                return task.planView.appliedCategoryIds.includes(value);
            });
        case FilterFields.GoalIds:
            return filter.values.some((value)=>{
                if (value === "" || value === TasksWithoutGoalAssociationGroup) {
                    return task.goalIds.length === 0;
                }
                return task.goalIds.includes(value);
            });
        case DynamicFilterField:
            return filter.values.some((value)=>task.id === value);
        case "displayName":
            // display name not in filterField as it's a separate keyword filter instead of filter list
            return filter.values.some((value)=>{
                const valueToLower = value.toLocaleLowerCase();
                const taskTitleToLower = task.displayName.toLocaleLowerCase();
                try {
                    const escapedValue = valueToLower.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
                    // eslint-disable-next-line security/detect-non-literal-regexp
                    const regex = new RegExp(escapedValue);
                    return taskTitleToLower.search(regex) >= 0 || matchesCustomField(regex, task.customFieldValues, choiceDisplayNames, cfdValueTypes);
                } catch (e) {
                    return taskTitleToLower.indexOf(valueToLower) >= 0 || matchesCustomField(valueToLower, task.customFieldValues, choiceDisplayNames, cfdValueTypes);
                }
            });
        default:
            {
                const definitionId = resolveCustomFieldDefinitionIdFromFilterField(filter.field);
                if (definitionId != null) {
                    return matchesCustomFieldFilter(task, filter, todayPlainDate, timeZone, cfdValueTypes, definitionId);
                }
                return true; // If the filter field is not recognized, we treat it as a pass-through (no filtering).
            }
    }
}
/**
 * Applies a custom field filter to a task by matching the task's custom field value
 * against the filter's expected values. Handles Flag, Date, and SingleChoiceText value types.
 * @param task - Task to check
 * @param filter - Filter whose field is a custom field ID
 * @param todayPlainDate - Pre-computed today's PlainDate for date category comparison
 * @param timeZone - Timezone used to resolve the local PlainDate of IDateWithOptionalTime values
 * @param cfdValueTypes - Map from custom field definition ID to its value type for the current plan
 * @param definitionId - The resolved custom field definition ID (prefix already stripped)
 * @returns True if the task's custom field value matches any of the filter values
 */ function matchesCustomFieldFilter(task, filter, todayPlainDate, timeZone, cfdValueTypes, definitionId) {
    const valueType = cfdValueTypes.get(definitionId);
    const cfvIndex = task.customFieldValues.findIndex((cfv)=>{
        return cfv.customFieldDefinitionId === definitionId;
    });
    const cfv = cfvIndex >= 0 ? task.customFieldValues[cfvIndex] : null;
    return filter.values.some((filterValue)=>{
        switch(valueType){
            case CustomFieldValueType.Flag:
                {
                    if (filterValue === NoCustomFieldValueFilterKey) {
                        return isCustomFieldValueEmpty(cfv);
                    }
                    const filterValueString = filterValue ? "true" : "false";
                    return !!cfv && cfv.value === filterValueString;
                }
            case CustomFieldValueType.Date:
                try {
                    const customFieldDate = cfv ? Temporal.PlainDate.from(cfv.value) : null;
                    const filterValueDateCategory = filterValue;
                    return customFieldDate ? getDueDateCategory(customFieldDate, todayPlainDate) === filterValueDateCategory : filterValueDateCategory === DueDateCategory.NoDate;
                } catch  {
                    return false;
                }
            case CustomFieldValueType.SingleChoiceText:
                if (filterValue === NoCustomFieldValueFilterKey) {
                    return isCustomFieldValueEmpty(cfv);
                }
                return !!cfv && cfv.value === filterValue;
            case CustomFieldValueType.Text:
            case CustomFieldValueType.Number:
            default:
                return false;
        }
    });
}
/**
 * Determines whether a custom field value is unset/empty.
 * A task is considered to have "no value" for a custom field when there is no value entry
 * for the field.
 * @param cfv The task's custom field value for the field, or null if none exists
 * @returns True if the custom field has no value set
 */ function isCustomFieldValueEmpty(cfv) {
    return cfv == null;
}
const matchesCustomField = (matcher, customFieldValues, choiceDisplayNames, cfdValueTypes)=>{
    const test = (value)=>{
        if (matcher instanceof RegExp) {
            return matcher.test(value.toLocaleLowerCase());
        }
        return value.toLocaleLowerCase().includes(matcher.toLocaleLowerCase());
    };
    return customFieldValues.some((cfv)=>{
        switch(cfdValueTypes.get(cfv.customFieldDefinitionId)){
            case CustomFieldValueType.Text:
            case CustomFieldValueType.Number:
                return test(cfv.value);
            case CustomFieldValueType.Flag:
                {
                    // Match against locale Yes/No strings as rendered in the grid
                    const flagDisplay = cfv.value === "true" ? Strings.CustomFieldFlag_YesText : cfv.value === "false" ? Strings.CustomFieldFlag_NoText : "";
                    return flagDisplay ? test(flagDisplay) : false;
                }
            case CustomFieldValueType.Date:
                // Use the same locale-aware short date format as the grid renders (e.g. "6/15/2025")
                return test(formatShortDate(cfv.value));
            case CustomFieldValueType.SingleChoiceText:
                {
                    // When choice display names are available, match against the human-readable display name
                    // instead of the internal choice ID.
                    const displayName = choiceDisplayNames.get(cfv.value);
                    return displayName ? test(displayName) : false;
                }
            default:
                return false;
        }
    });
};
